import { Dayjs } from 'dayjs';
import { QRCodeSVG } from 'qrcode.react';

import { Logo } from 'src/Components/Logo';
import { LocalEvent } from 'src/Hooks/Queries/useFetchEvents';
import { EventDtoTypeEnum } from 'src/Services/API/generated';
import { FALLBACK_HOUSEHOLD_COLOR, getEventVisual } from 'src/Utils/eventVisuals';

import { Props } from './PrintablePlanning.types';

const PAPER = '#FFFFFF';
const INK = '#1B1B1B';
const STONE = '#4A4A4A';
const BORDER = '#1B1B1B';
const WEEKEND_BG = 'rgba(0, 0, 0, 0.03)';
const VAC_HATCHING =
  'repeating-linear-gradient(-45deg, rgba(0,0,0,0.08) 0 3px, transparent 3px 8px)';
const FERIE_BG = 'rgba(195, 114, 91, 0.15)';

const SANS = "'Inter', Helvetica, Arial, sans-serif";

const buildDays = (startDate: Dayjs, endDate: Dayjs): Dayjs[] => {
  const days: Dayjs[] = [];
  let day = startDate.startOf('day');
  const last = endDate.startOf('day');
  while (day.isBefore(last) || day.isSame(last, 'day')) {
    days.push(day);
    day = day.add(1, 'day');
  }
  return days;
};

const eventsForDay = (events: LocalEvent[], day: Dayjs): LocalEvent[] => {
  const dayStart = day.startOf('day');
  const dayEnd = day.endOf('day');
  return events
    .filter(event => {
      if (event.endTime) return event.startTime.isBefore(dayEnd) && event.endTime.isAfter(dayStart);
      return event.startTime.isSame(day, 'day');
    })
    .sort((a, b) => a.startTime.diff(b.startTime));
};

interface HouseholdLegendEntry {
  key: string;
  color: string;
  name: string;
}

const collectHouseholds = (events: LocalEvent[]): HouseholdLegendEntry[] => {
  const seen = new Map<string, HouseholdLegendEntry>();
  events.forEach(event => {
    if (event.type !== EventDtoTypeEnum.Occupation && event.type !== EventDtoTypeEnum.Location) {
      return;
    }
    if (event.household) {
      if (!seen.has(event.household.id)) {
        seen.set(event.household.id, {
          key: event.household.id,
          color: event.household.color ?? FALLBACK_HOUSEHOLD_COLOR,
          name: event.household.name,
        });
      }
    } else {
      const key = `_no_household_${event.type}`;
      if (!seen.has(key)) {
        seen.set(key, { key, color: FALLBACK_HOUSEHOLD_COLOR, name: event.name });
      }
    }
  });
  return Array.from(seen.values());
};

const PrintablePlanning = ({
  events,
  startDate,
  endDate,
  view,
  assetName,
  exportUrl,
  dateRangeLabel,
  weekDayLabels,
  activeEventTypes,
  printOptions,
  todaySummary,
  printedOnLabel,
}: Props) => {
  const days = buildDays(startDate, endDate);
  const midDay = days[Math.floor(days.length / 2)] ?? startDate;
  const referenceMonth = midDay.month();

  const weeks: Dayjs[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  const filteredEvents = events.filter(event => {
    if (activeEventTypes.length > 0 && !activeEventTypes.includes(event.type)) return false;
    const visual = getEventVisual(event);
    if (visual.kind === 'reminder' && !printOptions.showReminders) return false;
    if (visual.kind === 'marker') {
      if (event.type === EventDtoTypeEnum.PublicHoliday && !printOptions.showPublicHolidays) {
        return false;
      }
      const schoolTypes: readonly EventDtoTypeEnum[] = [
        EventDtoTypeEnum.SchoolHolidayZoneA,
        EventDtoTypeEnum.SchoolHolidayZoneB,
        EventDtoTypeEnum.SchoolHolidayZoneC,
      ];
      if (schoolTypes.includes(event.type) && !printOptions.showSchoolHolidays) return false;
    }
    return true;
  });

  const householdLegend = collectHouseholds(filteredEvents);

  return (
    <div
      style={{
        width: '100%',
        fontFamily: SANS,
        boxSizing: 'border-box',
        backgroundColor: PAPER,
        color: INK,
      }}
    >
      <div
        style={{
          padding: '14px 24px 10px',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: '20px',
          borderBottom: `1.5px solid ${BORDER}`,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Logo variant="wordmark" alt="ShareHeritage" style={{ height: 22, width: 'auto' }} />
          <span style={{ fontSize: '10px', color: STONE, letterSpacing: '0.5px' }}>
            {printedOnLabel} · {todaySummary}
          </span>
        </div>
        <span
          style={{
            fontSize: '22px',
            fontWeight: 700,
            color: INK,
            textAlign: 'center',
            textTransform: 'capitalize',
          }}
        >
          {dateRangeLabel}
        </span>
        <span
          style={{
            fontSize: '12px',
            fontWeight: 600,
            color: STONE,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            textAlign: 'right',
          }}
        >
          {assetName}
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          padding: '0 24px',
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        {weekDayLabels.map((label, idx) => (
          <div
            key={`${label}-${idx}`}
            style={{
              padding: '6px 4px',
              textAlign: 'center',
              fontSize: '11px',
              fontWeight: 700,
              color: INK,
              textTransform: 'uppercase',
              letterSpacing: '1px',
            }}
          >
            {label}
          </div>
        ))}
      </div>

      <div style={{ padding: '0 24px' }}>
        {weeks.map((week, weekIdx) => (
          <div
            key={weekIdx}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              borderBottom: `1px solid ${BORDER}`,
              breakInside: 'avoid',
              pageBreakInside: 'avoid',
            }}
          >
            {week.map((day, dayIdx) => {
              const dayEvents = eventsForDay(filteredEvents, day);
              const isOutOfMonth = view === 'month' && day.month() !== referenceMonth;
              const isWeekend = day.day() === 0 || day.day() === 6;

              let isSchoolVacation = false;
              let isPublicHoliday = false;
              const ribbons: { event: LocalEvent; color: string; label: string }[] = [];
              const chips: {
                event: LocalEvent;
                icon: string;
                label: string;
                isReminder: boolean;
              }[] = [];

              dayEvents.forEach(event => {
                const visual = getEventVisual(event);
                if (visual.kind === 'marker') {
                  if (event.type === EventDtoTypeEnum.PublicHoliday) isPublicHoliday = true;
                  else isSchoolVacation = true;
                } else if (visual.kind === 'ribbon') {
                  ribbons.push({
                    event,
                    color: visual.color ?? FALLBACK_HOUSEHOLD_COLOR,
                    label: visual.label,
                  });
                } else if (visual.kind === 'chip') {
                  chips.push({
                    event,
                    icon: '•',
                    label: visual.label,
                    isReminder: false,
                  });
                } else if (visual.kind === 'reminder') {
                  chips.push({ event, icon: '🔔', label: visual.label, isReminder: true });
                }
              });

              const cellBg = isPublicHoliday
                ? FERIE_BG
                : isSchoolVacation
                  ? VAC_HATCHING
                  : isWeekend
                    ? WEEKEND_BG
                    : PAPER;

              return (
                <div
                  key={day.format('YYYY-MM-DD')}
                  style={{
                    minHeight: '80px',
                    padding: '4px 6px',
                    borderRight: dayIdx < 6 ? `1px solid ${BORDER}` : 'none',
                    background: cellBg,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '3px',
                    opacity: isOutOfMonth ? 0.35 : 1,
                    boxSizing: 'border-box',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        fontSize: '12px',
                        fontWeight: 700,
                        color: isPublicHoliday ? PAPER : INK,
                        backgroundColor: isPublicHoliday ? INK : 'transparent',
                      }}
                    >
                      {day.date()}
                    </span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {ribbons.map(({ event, color, label }) => (
                      <div
                        key={event.localId}
                        style={{
                          fontSize: '9.5px',
                          fontWeight: 600,
                          padding: '2px 5px',
                          border: `1.5px solid ${BORDER}`,
                          borderRadius: '3px',
                          background: color,
                          color: PAPER,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {label}
                      </div>
                    ))}
                    {chips.map(({ event, icon, label, isReminder }) => (
                      <div
                        key={event.localId}
                        style={{
                          fontSize: isReminder ? '9px' : '9.5px',
                          fontStyle: isReminder ? 'italic' : 'normal',
                          color: isReminder ? STONE : INK,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        <span style={{ flexShrink: 0 }}>{icon}</span>
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div
        style={{
          padding: '10px 24px 12px',
          borderTop: `1.5px solid ${BORDER}`,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '16px',
          marginTop: '12px',
        }}
      >
        {printOptions.showHouseholdLegend && householdLegend.length > 0 ? (
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              fontSize: '10px',
              color: INK,
            }}
          >
            {householdLegend.map(entry => (
              <div
                key={entry.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    width: '12px',
                    height: '12px',
                    background: entry.color,
                    border: `1px solid ${BORDER}`,
                  }}
                />
                <span>{entry.name}</span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ flex: 1 }} />
        )}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              padding: '3px',
              backgroundColor: PAPER,
              border: `1px solid ${BORDER}`,
              lineHeight: 0,
            }}
          >
            <QRCodeSVG value={exportUrl} size={60} level="M" fgColor={INK} bgColor={PAPER} />
          </div>
          <Logo variant="icon" alt="" style={{ height: 36, width: 'auto', flexShrink: 0 }} />
        </div>
      </div>
    </div>
  );
};

export default PrintablePlanning;
